'use client';

import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Header } from '@/components/layout';
import { Button, Card, CardHeader, Input } from '@/components/ui';
import { api, ApiError } from '@/lib/api';
import { useAuth } from '@/providers/AuthProvider';

const loginSchema = z.object({
    email: z.email(),
    password: z.string().min(1),
    rememberMe: z.boolean(),
});

type LoginFormData = z.infer<typeof loginSchema>;

export default function LoginPage() {
    const t = useTranslations('auth.login');
    const tValidation = useTranslations('validation');
    const router = useRouter();
    const searchParams = useSearchParams();
    const [error, setError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<LoginFormData>({
        resolver: zodResolver(loginSchema),
        defaultValues: {
            email: '',
            password: '',
            rememberMe: false,
        },
    });

    const { refreshUser } = useAuth();

    const onSubmit = async (data: LoginFormData) => {
        setIsLoading(true);
        setError(null);

        try {
            await api.login(data.email, data.password, data.rememberMe);
            await refreshUser();
            const next = searchParams.get('next');
            router.push(next || '/');
            router.refresh();
        } catch (err) {
            if (err instanceof ApiError && err.code) {
                // @ts-ignore
                setError(t(`errors.${err.code}`, err.params));
            } else {
                setError(err instanceof Error ? err.message : 'Login failed');
            }
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>

            <main className="min-h-screen flex items-center justify-center px-4 pt-24 pb-12 bg-[#F9F9F9]">
                <div className="w-full max-w-md">
                    <div className="mb-8 text-center">
                        <h1 className="text-2xl font-medium text-black mb-2">{t('title')}</h1>
                        <p className="text-[#888888]">{t('subtitle')}</p>
                    </div>

                    <Card className="w-full border-none shadow-none bg-transparent p-0">
                        {error && (
                            <div className="mb-6 p-4 bg-red-50 text-red-600 text-sm">
                                {error}
                            </div>
                        )}

                        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
                            <Input
                                label={t('email')}
                                type="email"
                                autoComplete="email"
                                {...register('email')}
                                error={errors.email ? tValidation('email') : undefined}
                                className="bg-white"
                            />

                            <div className="relative">
                                <Input
                                    label={t('password')}
                                    type={showPassword ? 'text' : 'password'}
                                    autoComplete="current-password"
                                    {...register('password')}
                                    error={errors.password ? tValidation('required') : undefined}
                                    className="bg-white pr-11"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(v => !v)}
                                    className="absolute right-3 top-8 text-slate-400 hover:text-slate-600 transition-colors cursor-pointer"
                                    tabIndex={-1}
                                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                                >
                                    {showPassword ? (
                                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                                        </svg>
                                    ) : (
                                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                        </svg>
                                    )}
                                </button>
                            </div>

                            <label className="flex items-center gap-3 cursor-pointer">
                                <input
                                    type="checkbox"
                                    {...register('rememberMe')}
                                    className="w-4 h-4 rounded-xs border-slate-300 text-black focus:ring-black"
                                />
                                <span className="text-sm text-slate-600">{t('rememberMe')}</span>
                            </label>

                            <Button type="submit" className="w-full" isLoading={isLoading}>
                                {t('submit')}
                            </Button>
                        </form>

                        <div className="mt-8 text-center space-y-4">
                            <div>
                                <Link
                                    href="/reset-password"
                                    className="text-sm text-black hover:underline"
                                >
                                    {t('forgotPassword')}
                                </Link>
                            </div>
                            <p className="text-sm text-[#888888]">
                                {t('noAccount')}{' '}
                                <Link
                                    href="/signup"
                                    className="text-black font-medium hover:underline"
                                >
                                    {t('signupLink')}
                                </Link>
                            </p>
                        </div>
                    </Card>
                </div>
            </main>
        </>
    );
}
